import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Truck, Package, MapPin, Clock, Shield, Building, ArrowRight } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";

const services = [
  { icon: Truck, title: "Express Delivery", price: "From ₹99", desc: "Same-day and next-day delivery for urgent shipments. Available in 50+ major cities.", features: ["Same-day pickup", "Priority handling", "Real-time updates"] },
  { icon: Package, title: "Standard Parcel", price: "From ₹49", desc: "Affordable door-to-door parcel delivery within 2-5 business days across India.", features: ["Nationwide coverage", "Weight up to 50kg", "Tracking included"] },
  { icon: MapPin, title: "Last Mile Delivery", price: "Custom", desc: "Efficient last-mile delivery solutions for e-commerce businesses and enterprises.", features: ["Bulk discounts", "API integration", "Dedicated agent"] },
  { icon: Clock, title: "Scheduled Pickup", price: "From ₹79", desc: "Book a pickup slot at your convenience and we'll come to your doorstep.", features: ["Flexible slots", "Home pickup", "Doorstep service"] },
  { icon: Shield, title: "Secured Shipping", price: "+ ₹29", desc: "Additional insurance coverage for high-value items and fragile packages.", features: ["Full value cover", "Fragile handling", "Priority packaging"] },
  { icon: Building, title: "Business Accounts", price: "Contact Us", desc: "Dedicated account management and customized pricing for businesses shipping 100+ orders/month.", features: ["Volume discounts", "API access", "Account manager"] },
];

export default function Services() {
  return (
    <PublicLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-3">What We Offer</p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white uppercase">Our Services</h1>
            <p className="text-blue-200 mt-4 text-lg">Comprehensive logistics solutions tailored to your needs.</p>
          </div>
        </div>

        {/* Services Grid */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-brand-500 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
                      <s.icon className="w-6 h-6 text-brand-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-sm font-bold text-brand-500 bg-brand-50 px-3 py-1 rounded-full">{s.price}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <ul className="space-y-1.5">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-brand-500">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="font-heading text-4xl font-bold text-white uppercase mb-4">Start Shipping Today</h2>
            <p className="text-blue-100 mb-8">Create a free account and get access to all our services with transparent pricing.</p>
            <Link
              to="/book"
              data-testid="services-book-btn"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#F97316] text-white font-semibold uppercase rounded-sm hover:bg-orange-600 transition-colors"
            >
              Book Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
